import * as React from 'react';
import Box from '@mui/material/Box';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Button from '@mui/material/Button';
import { FormGroup, TextField, Typography } from '@mui/material';
import protectAxios from '../../../api/axios';
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { ISentinelDetails, IValidSentinelInstance } from '../SignupPage';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  minHeight: '300px',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

interface SignupStep1Props {
  onNextClick: (sentinelDetails: IValidSentinelInstance) => void;
  onPreviousClick: VoidFunction;
  formData : IValidSentinelInstance;
}

export const SignupStep1 : React.FC<SignupStep1Props> = (props) => {

  const validationSchema = yup.object().shape({
    subscriptionId: yup
      .string()
      .uuid("Subscription id has to be a valid UUID")
  });

  const { control, handleSubmit,  formState: { errors } } = useForm<ISentinelDetails>({ resolver: yupResolver(validationSchema) });
  const [ checkForSentinelInvalid, setCheckForSentinelInvalid ] = React.useState(false);

  const onSubmit: SubmitHandler<ISentinelDetails> = async (data) => {
    try
    {
        setCheckForSentinelInvalid(false);

        const response = await protectAxios
        .get(`/companyqueries/getSentinelResources/${data.subscriptionId}`)
        .then((resp) => 
        {
            var sentinelResult = resp.data[0];
            var result : IValidSentinelInstance = {
                subscriptionId :  data.subscriptionId,
                resourceGroupName : sentinelResult.resourceGroupName,
                workspaceId: sentinelResult.workspaceId,
                workspaceName: sentinelResult.workspaceName
            }
            props.onNextClick(result);            
        });
    }
    catch (errorResponse)
    {
        setCheckForSentinelInvalid(true);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
      <Card variant="outlined">
        <Box sx={{ display: { xs: 'flex' } }}>
          <img src="/logo.png" alt="Logo" style={{ height: 40, paddingBottom:0 }} />  
        </Box>

        <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
            >
          Please enter your subscription id
        </Typography>

        <Controller
            name="subscriptionId"
            control={control}
            defaultValue={
               props.formData.subscriptionId === null
                 ? undefined
                 : props.formData.subscriptionId
            }
            rules={{
              required: {
                value: true,
                message: "",
              },
            }}
            render={({ field }) => (
              <TextField
                name={field.name}
                value={field.value}
                size="small"
                required
                onChange={field.onChange}
                id="subscriptionid"
                label="Subscription Id"
                sx={{ width: "100%", mt: 2 }}
                helperText={
                    errors.subscriptionId ? (
                    <Typography variant="subtitle2">
                        {errors.subscriptionId?.message}
                    </Typography>
                    ) : (
                    <></>
                    )
                }
                error={errors.subscriptionId !== undefined}
              />
            )}
          />
            { checkForSentinelInvalid ? 
                <Typography variant="subtitle1" color={'red'}>
                    We couldn't validate your sentinel instance, please try again.
                </Typography> : 
                <></>
            }
         <FormGroup row sx={{justifyContent:'flex-end'}}>
            <Button
                variant="outlined"
                color="primary"
                sx={{ mt: 2, mr: 2 }}
                onClick={props.onPreviousClick}
            >
                Previous
            </Button>
            <Button
                variant="outlined"
                color="primary"
                type="submit"
                sx={{ mt: 2 }}
            >
                Next
            </Button>
        </FormGroup>

      </Card>
    </form>
  )
}